import React, { useMemo } from 'react';
import { Badge, Box, Flex, SimpleGrid, Text } from '@chakra-ui/react';
import { GenderIntersex, UserList } from 'phosphor-react';

import { GlassCard } from '../../../../components/glass';
import { IFolhaColaborador } from '../../../../hooks/useFinanceiro';
import { AnimatedBar } from '../components/AnimatedBar';
import { brl } from '../components/format';

const fmtPct = (v: number) => `${v.toFixed(0)}%`;

// Arquétipos de cargo (heurística por palavra-chave) — agrupam os cargos crus
// do Domínio em áreas de trabalho legíveis, à la "Pessoal" do Auditoria.
const ARQUETIPOS: { label: string; cor: string; match: RegExp }[] = [
  { label: 'Contábil', cor: '#1B88F5', match: /cont[áa]b/i },
  { label: 'Fiscal', cor: '#7C3AED', match: /fiscal|tribut/i },
  { label: 'Depto Pessoal', cor: '#DB2777', match: /pessoal|folha|trabalhista|\bdp\b/i },
  { label: 'Societário/Legal', cor: '#0891B2', match: /societ|jur[íi]d|legal|paralegal/i },
  { label: 'Sucesso/Atendimento', cor: '#16A34A', match: /sucesso|atendimento|relacion/i },
  {
    label: 'Gestão/Liderança',
    cor: '#D97706',
    match: /gerente|coordena|supervis|diretor|l[íi]der|gestor|head/i,
  },
  { label: 'Tecnologia/Dados', cor: '#0EA5E9', match: /desenvolv|dados|sistema|software|\bti\b/i },
  {
    label: 'Adm/Serviços',
    cor: '#64748B',
    match: /auxiliar|servi[çc]os gerais|recep|administrat|secret|copa|limpeza/i,
  },
];
const OUTROS = { label: 'Outros', cor: '#A1A1AA' };
const classify = (cargo: string | null) =>
  ARQUETIPOS.find(a => a.match.test(cargo || '')) ?? OUTROS;

const anosDeCasa = (admissao: string | null) => {
  if (!admissao) return null;
  const [y, m, d] = admissao.split('-').map(Number);
  const adm = new Date(y, (m || 1) - 1, d || 1);
  return (Date.now() - adm.getTime()) / (365.25 * 86400000);
};

const FAIXAS_TEMPO: { label: string; test: (a: number) => boolean }[] = [
  { label: '< 1 ano', test: a => a < 1 },
  { label: '1–3 anos', test: a => a >= 1 && a < 3 },
  { label: '3–5 anos', test: a => a >= 3 && a < 5 },
  { label: '5–10 anos', test: a => a >= 5 && a < 10 },
  { label: '10+ anos', test: a => a >= 10 },
];

const DistRow = ({
  label,
  valorLabel,
  pct,
  cor,
}: {
  label: string;
  valorLabel: string;
  pct: number;
  cor: string;
}) => (
  <Box>
    <Flex justify="space-between" align="center" fontSize="sm" mb={1} gap={2}>
      <Text color="#0F172A" noOfLines={1}>
        {label}
      </Text>
      <Text color="#64748B" whiteSpace="nowrap" fontSize="xs">
        {valorLabel}
      </Text>
    </Flex>
    <AnimatedBar pct={pct > 0 ? Math.max(3, pct) : 0} cor={cor} />
  </Box>
);

interface PessoasAnalyticsProps {
  colaboradores: IFolhaColaborador[];
}

export const PessoasAnalytics = ({ colaboradores }: PessoasAnalyticsProps) => {
  // A lista já vem filtrada por status (ativo/demitido/todos) pelo PessoasTab.
  const pessoas = colaboradores;

  const porArquetipo = useMemo(() => {
    const map = new Map<string, { cor: string; qtd: number; sal: number }>();
    pessoas.forEach(c => {
      const a = classify(c.cargo);
      const cur = map.get(a.label) ?? { cor: a.cor, qtd: 0, sal: 0 };
      cur.qtd += 1;
      cur.sal += c.salario || 0;
      map.set(a.label, cur);
    });
    return Array.from(map.entries())
      .map(([label, v]) => ({ label, ...v, medio: v.qtd ? v.sal / v.qtd : 0 }))
      .sort((a, b) => b.qtd - a.qtd);
  }, [pessoas]);

  const porDepto = useMemo(() => {
    const map = new Map<string, { qtd: number; sal: number }>();
    pessoas.forEach(c => {
      const d = c.departamento || '—';
      const cur = map.get(d) ?? { qtd: 0, sal: 0 };
      cur.qtd += 1;
      cur.sal += c.salario || 0;
      map.set(d, cur);
    });
    return Array.from(map.entries())
      .map(([label, v]) => ({ label, ...v, medio: v.qtd ? v.sal / v.qtd : 0 }))
      .sort((a, b) => b.qtd - a.qtd)
      .slice(0, 8);
  }, [pessoas]);

  const genero = useMemo(() => {
    let m = 0;
    let fem = 0;
    let na = 0;
    pessoas.forEach(c => {
      const s = (c.sexo || '').toUpperCase();
      if (s === 'M') m += 1;
      else if (s === 'F') fem += 1;
      else na += 1;
    });
    return { m, fem, na, tot: pessoas.length };
  }, [pessoas]);

  const tempoCasa = useMemo(() => {
    const counts = FAIXAS_TEMPO.map(() => 0);
    pessoas.forEach(c => {
      const a = anosDeCasa(c.admissao);
      if (a == null) return;
      const idx = FAIXAS_TEMPO.findIndex(f => f.test(a));
      if (idx >= 0) counts[idx] += 1;
    });
    return counts;
  }, [pessoas]);

  const maxArq = Math.max(1, ...porArquetipo.map(a => a.qtd));
  const maxDep = Math.max(1, ...porDepto.map(d => d.qtd));
  const maxTempo = Math.max(1, ...tempoCasa);
  const genTot = Math.max(1, genero.tot);

  if (pessoas.length === 0) {
    return (
      <GlassCard intensity="medium" p={10} textAlign="center">
        <Text color="#71717A">Sem colaboradores para o filtro selecionado.</Text>
      </GlassCard>
    );
  }

  return (
    <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={5}>
      {/* Por área de trabalho (arquétipo) */}
      <GlassCard intensity="medium" p={5}>
        <Flex align="center" gap={2} mb={1}>
          <UserList size={18} color="#1B88F5" weight="duotone" />
          <Text fontWeight={700} color="#0F172A">
            Equipe por área
          </Text>
        </Flex>
        <Text fontSize="11px" color="#94A3B8" mb={4}>
          Cargos do Domínio agrupados · headcount e salário médio
        </Text>
        <Flex direction="column" gap={3}>
          {porArquetipo.map(a => (
            <DistRow
              key={a.label}
              label={a.label}
              valorLabel={`${a.qtd} · ${brl(a.medio)}`}
              pct={(a.qtd / maxArq) * 100}
              cor={a.cor}
            />
          ))}
        </Flex>
      </GlassCard>

      {/* Por departamento */}
      <GlassCard intensity="medium" p={5}>
        <Text fontWeight={700} color="#0F172A" mb={1}>
          Por departamento
        </Text>
        <Text fontSize="11px" color="#94A3B8" mb={4}>
          Top 8 · headcount e salário médio
        </Text>
        <Flex direction="column" gap={3}>
          {porDepto.map(d => (
            <DistRow
              key={d.label}
              label={d.label}
              valorLabel={`${d.qtd} · ${brl(d.medio)}`}
              pct={(d.qtd / maxDep) * 100}
              cor="#DB2777"
            />
          ))}
        </Flex>
      </GlassCard>

      {/* Tempo de casa */}
      <GlassCard intensity="medium" p={5}>
        <Text fontWeight={700} color="#0F172A" mb={1}>
          Tempo de casa
        </Text>
        <Text fontSize="11px" color="#94A3B8" mb={4}>
          Anos desde a admissão
        </Text>
        <Flex direction="column" gap={3}>
          {FAIXAS_TEMPO.map((f, i) => (
            <DistRow
              key={f.label}
              label={f.label}
              valorLabel={`${tempoCasa[i]}`}
              pct={(tempoCasa[i] / maxTempo) * 100}
              cor="#0EA5E9"
            />
          ))}
        </Flex>
      </GlassCard>

      {/* Gênero */}
      <GlassCard intensity="medium" p={5}>
        <Flex align="center" gap={2} mb={1}>
          <GenderIntersex size={18} color="#7C3AED" weight="duotone" />
          <Text fontWeight={700} color="#0F172A">
            Gênero
          </Text>
        </Flex>
        <Text fontSize="11px" color="#94A3B8" mb={4}>
          Distribuição por gênero
        </Text>
        <Flex h="14px" borderRadius="full" overflow="hidden" mb={3}>
          {genero.fem > 0 && <Box bg="#DB2777" w={`${(genero.fem / genTot) * 100}%`} />}
          {genero.m > 0 && <Box bg="#1B88F5" w={`${(genero.m / genTot) * 100}%`} />}
          {genero.na > 0 && <Box bg="#CBD5E1" w={`${(genero.na / genTot) * 100}%`} />}
        </Flex>
        <Flex gap={4} wrap="wrap" fontSize="sm">
          <Flex align="center" gap={1.5}>
            <Box w="10px" h="10px" borderRadius="sm" bg="#DB2777" />
            <Text color="#0F172A">
              Feminino <b>{genero.fem}</b> · {fmtPct((genero.fem / genTot) * 100)}
            </Text>
          </Flex>
          <Flex align="center" gap={1.5}>
            <Box w="10px" h="10px" borderRadius="sm" bg="#1B88F5" />
            <Text color="#0F172A">
              Masculino <b>{genero.m}</b> · {fmtPct((genero.m / genTot) * 100)}
            </Text>
          </Flex>
          {genero.na > 0 && (
            <Flex align="center" gap={1.5}>
              <Box w="10px" h="10px" borderRadius="sm" bg="#CBD5E1" />
              <Text color="#64748B">Não inf. {genero.na}</Text>
            </Flex>
          )}
        </Flex>
      </GlassCard>
    </SimpleGrid>
  );
};

// Tabela-roster reutilizável (lista de colaboradores do Domínio).
export const FolhaRoster = ({ colaboradores }: PessoasAnalyticsProps) => (
  <GlassCard intensity="strong" p={{ base: 3, md: 5 }}>
    <Flex
      fontSize="10px"
      fontWeight={700}
      color="#94A3B8"
      textTransform="uppercase"
      px={2}
      pb={2}
      gap={3}
    >
      <Text flex="1">Colaborador</Text>
      <Text w="120px" textAlign="right">
        Salário
      </Text>
      <Text w="90px" textAlign="right">
        Status
      </Text>
    </Flex>
    <Box maxH="520px" overflowY="auto">
      {colaboradores.map(c => {
        const ativo = (c.status || '').toLowerCase().startsWith('ativ');
        return (
          <Flex
            key={`${c.nome}-${c.franquia}-${c.cargo}-${c.salario}-${c.status}`}
            align="center"
            gap={3}
            px={2}
            py={2}
            borderRadius="md"
            _hover={{ bg: 'rgba(15,23,42,0.03)' }}
          >
            <Box flex="1" minW={0}>
              <Text fontSize="sm" fontWeight={600} color="#0F172A" noOfLines={1}>
                {c.nome || '—'}
              </Text>
              <Text fontSize="11px" color="#71717A" noOfLines={1}>
                {c.cargo || '—'}
                {c.departamento ? ` · ${c.departamento}` : ''}
                {c.franquia ? ` · ${c.franquia}` : ''}
              </Text>
            </Box>
            <Text
              w="120px"
              textAlign="right"
              fontSize="sm"
              fontWeight={700}
              color={ativo ? '#0F172A' : '#94A3B8'}
              whiteSpace="nowrap"
            >
              {c.salario != null ? brl(c.salario) : '—'}
            </Text>
            <Box w="90px" textAlign="right">
              <Badge colorScheme={ativo ? 'green' : 'red'} fontSize="9px" variant="subtle">
                {ativo ? 'Ativo' : 'Demitido'}
              </Badge>
            </Box>
          </Flex>
        );
      })}
      {colaboradores.length === 0 && (
        <Text fontSize="sm" color="#94A3B8" textAlign="center" py={6}>
          Sem colaboradores do escritório no Domínio.
        </Text>
      )}
    </Box>
  </GlassCard>
);
